import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import FormSubmissionsTab from './FormSubmissionsTab';
import FormFieldsEditor, { type FormField } from './FormFieldsEditor';
import { useEntitlements } from '../../hooks/useEntitlements';

// ── Share Panel ───────────────────────────────────────────────────────────────
function SharePanel({ instanceId, campaignName, isDraft }: { instanceId: string; campaignName: string; isDraft?: boolean }) {
    const publicUrl = `${window.location.origin}/preview/instance/${instanceId}`;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicUrl)}&bgcolor=ffffff&color=000000&margin=12`;
    const [copied, setCopied] = useState(false);
    const [qrError, setQrError] = useState(false);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback
            const el = document.createElement('textarea');
            el.value = publicUrl;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const downloadQR = () => {
        const a = document.createElement('a');
        a.href = qrUrl;
        a.download = `qr-${campaignName.replace(/\s+/g, '-')}.png`;
        a.target = '_blank';
        a.click();
    };

    return (
        <div className="mb-5 overflow-hidden rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                {/* QR Code */}
                <div className="flex-shrink-0">
                    <div className="relative flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-white shadow-md">
                        {!qrError ? (
                            <img
                                src={qrUrl}
                                alt="QR code"
                                className="h-full w-full object-contain"
                                onError={() => setQrError(true)}
                            />
                        ) : (
                            // Fallback: hiển thị icon QR khi không có internet
                            <div className="flex flex-col items-center justify-center gap-1 text-gray-400">
                                <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                        d="M12 4v1m0 14v1M4 12h1m14 0h1m-3.343-6.657.707.707M6.636 17.364l.707.707m0-12.728.707-.707m9.9 12.728.707-.707M9 12a3 3 0 116 0 3 3 0 01-6 0z" />
                                </svg>
                                <span className="text-xs">QR</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={downloadQR}
                        className="mt-1.5 w-full rounded-lg bg-white py-1 text-center text-xs text-blue-600 shadow-sm hover:bg-blue-50"
                    >
                        ⬇ Tải QR
                    </button>
                </div>

                {/* Link info */}
                <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                        {isDraft ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                Bản nháp / Tạm dừng (Chỉ dùng để test)
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                                Link công khai (Đang hoạt động)
                            </span>
                        )}
                    </div>
                    <p className="mb-2 text-sm font-medium text-gray-700">Chia sẻ link này để người chơi tham gia:</p>
                    <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 shadow-sm">
                        <span className="min-w-0 flex-1 truncate font-mono text-xs text-blue-700">{publicUrl}</span>
                        <button
                            onClick={copyLink}
                            className={`flex-shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                                copied
                                    ? 'bg-green-500 text-white'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                            {copied ? '✓ Đã copy' : 'Copy'}
                        </button>
                        <button
                            onClick={() => window.open(publicUrl, '_blank')}
                            className="flex-shrink-0 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                        >
                            ↗ Mở
                        </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-400">
                        Người chơi không cần tài khoản để tham gia. Link hoạt động trên mọi thiết bị.
                    </p>
                </div>
            </div>
        </div>
    );
}

interface Campaign {
    id?: string;
    name: string;
    slug: string;
    status?: string;
    description?: string;
    start_at?: string;
    end_at?: string;
    // max_plays_per_player removed — use cooldown instead
    win_rate?: number;
    play_cooldown_minutes?: number | null;
    kiosk_mode?: boolean;
    require_login?: boolean;
    require_form_before_play?: boolean;
    require_form_after_win?: boolean;
    remove_watermark?: boolean;
    form_fields?: FormField[];
    public_config?: Record<string, any>;
    private_config?: {
        anti_fraud?: {
            ip_limit_enabled?: boolean;
            ip_max_users_per_day?: number;
            ip_block_mode?: 'soft' | 'hard';
            blocked_ips?: Array<{ label: string; hash: string; note?: string; added_at?: string }>;
        };
        [key: string]: any;
    };
}

interface GameInstance {
    id: string;
    name: string;
    status: string;
    game_template?: {
        id: string;
        name: string;
        code: string;
        type?: string;
        game_type?: { code?: string; name?: string };
    };
    config?: Record<string, any> | null;
    assets?: Record<string, any> | null;
    created_at: string;
}

interface GameTemplate {
    id: string;
    name: string;
    code: string;
    version: string;
    type?: string;
    game_type?: { code?: string; name?: string };
}

interface Gift {
    id: string;
    code: string;
    name: string;
    status: string;
    total_quantity: number;
    remaining_quantity?: number;
    assigned_quantity: number;
    claimed_quantity: number;
    valid_until?: string | null;
    metadata?: Record<string, any>;
}

interface Player {
    id: string;
    full_name?: string | null;
    phone?: string | null;
    email?: string | null;
    identity_type?: string;
    last_seen_at?: string;
}

interface Metrics {
    total_players?: number;
    total_sessions?: number;
    total_plays?: number;
    total_rewards_awarded?: number;
    total_forms_submitted?: number;
    win_rate?: number;
    conversion_rate?: number;
    unique_players?: number;
    sessions_by_status?: Record<string, number>;
    rewards_by_type?: Record<string, number>;
    plays_by_day?: Record<string, number>;
}

type TabId = 'overview' | 'games' | 'gifts' | 'players' | 'forms' | 'analytics' | 'settings';
type CampaignStatus = 'draft' | 'configured' | 'scheduled' | 'active' | 'paused' | 'ended' | 'archived';

const emptyCampaign: Campaign = {
    name: '',
    slug: '',
    description: '',
    start_at: '',
    end_at: '',
    // max_plays_per_player removed
    require_login: false,
    require_form_before_play: false,
    require_form_after_win: true,
    remove_watermark: false,
};

const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';
const WORKSPACE_POLL_MS = 5000;

const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    configured: 'bg-blue-100 text-blue-800',
    scheduled: 'bg-purple-100 text-purple-800',
    active: 'bg-green-100 text-green-800',
    paused: 'bg-yellow-100 text-yellow-800',
    ended: 'bg-red-100 text-red-800',
    archived: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<string, string> = {
    draft: 'Bản nháp',
    configured: 'Đã cấu hình',
    scheduled: 'Đã lên lịch',
    active: 'Đang chạy',
    paused: 'Tạm dừng',
    ended: 'Đã kết thúc',
    archived: 'Lưu trữ',
};

const statusDescriptions: Record<string, string> = {
    draft: 'Chưa mở public play. Dùng để nhập thông tin, game và quà.',
    configured: 'Đã cấu hình nhưng chưa phát hành public.',
    scheduled: 'Đã lên lịch. Public play chỉ nên mở khi đến thời điểm chạy.',
    active: 'Đang mở public play. Preview instance sẽ gọi backend và trừ kho quà thật.',
    paused: 'Đang khóa public play. Preview instance không được quay local hoặc tạo thưởng giả.',
    ended: 'Chiến dịch đã kết thúc. Public play bị khóa để giữ số liệu báo cáo.',
    archived: 'Chiến dịch đã lưu trữ và không dùng cho vận hành.',
};

function getStatusLabel(status?: string) {
    return status ? statusLabels[status] || status : '-';
}

function getStatusDescription(status?: string) {
    return status ? statusDescriptions[status] || 'Trạng thái chưa được mô tả.' : 'Chưa có trạng thái.';
}

function formatDate(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

function formatDateTime(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function getRemaining(gift: Gift) {
    if (Number.isFinite(gift.remaining_quantity)) {
        return Math.max(0, Number(gift.remaining_quantity));
    }
    return Math.max(0, (gift.total_quantity || 0) - (gift.assigned_quantity || 0));
}

function getGameTypeCode(template?: GameInstance['game_template'] | GameTemplate) {
    return template?.code?.replace(/_v\d+$/, '') || template?.game_type?.code || template?.type || 'game';
}

function getGameLabel(template?: GameInstance['game_template'] | GameTemplate) {
    return template?.name || template?.game_type?.name || template?.code || 'Game';
}

function metricValue(value: number | undefined, suffix = '') {
    return `${Number(value || 0).toLocaleString()}${suffix}`;
}

function SummaryCard({ label, value, detail, tone = 'text-gray-900' }: { label: string; value: string; detail?: string; tone?: string }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
            <div className={`mt-2 text-2xl font-bold ${tone}`}>{value}</div>
            {detail && <div className="mt-1 text-xs text-gray-500">{detail}</div>}
        </div>
    );
}

function EmptyPanel({ title, description }: { title: string; description: string }) {
    return (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
    );
}

function CampaignStatusActions({
    status,
    onChange,
    disabled = false,
}: {
    status?: string;
    onChange: (status: CampaignStatus) => void;
    disabled?: boolean;
}) {
    const buttonClass = 'rounded-lg px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50';

    if (status === 'active') {
        return (
            <>
                <button type="button" disabled={disabled} onClick={() => onChange('paused')} className={`${buttonClass} bg-yellow-600 hover:bg-yellow-700`}>
                    Tạm dừng
                </button>
                <button type="button" disabled={disabled} onClick={() => onChange('ended')} className={`${buttonClass} bg-red-600 hover:bg-red-700`}>
                    Kết thúc
                </button>
            </>
        );
    }

    if (status === 'paused') {
        return (
            <>
                <button type="button" disabled={disabled} onClick={() => onChange('active')} className={`${buttonClass} bg-green-600 hover:bg-green-700`}>
                    Tiếp tục chạy
                </button>
                <button type="button" disabled={disabled} onClick={() => onChange('ended')} className={`${buttonClass} bg-red-600 hover:bg-red-700`}>
                    Kết thúc
                </button>
            </>
        );
    }

    if (status === 'scheduled') {
        return (
            <>
                <button type="button" disabled={disabled} onClick={() => onChange('active')} className={`${buttonClass} bg-green-600 hover:bg-green-700`}>
                    Kích hoạt ngay
                </button>
                <button type="button" disabled={disabled} onClick={() => onChange('paused')} className={`${buttonClass} bg-yellow-600 hover:bg-yellow-700`}>
                    Tạm dừng
                </button>
            </>
        );
    }

    if (status === 'ended') {
        return (
            <button type="button" disabled={disabled} onClick={() => onChange('active')} className={`${buttonClass} bg-green-600 hover:bg-green-700`}>
                Mở lại
            </button>
        );
    }

    return (
        <button type="button" disabled={disabled} onClick={() => onChange('active')} className={`${buttonClass} bg-green-600 hover:bg-green-700`}>
            Phát hành
        </button>
    );
}

// ── Helper: hash IP bằng SHA-256 (Web Crypto API) ────────────────────────────
async function sha256(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

// ── BlockedIpEntry type ───────────────────────────────────────────────────────
interface BlockedIpEntry {
    label: string;      // IP gốc admin nhập (chỉ để hiển thị)
    hash: string;       // SHA-256 hash — cái duy nhất được lưu vào DB / check backend
    note?: string;
    added_at?: string;
}

// ── BlockedIpManager component ────────────────────────────────────────────────
function BlockedIpManager({
    blockedIps,
    onChange,
}: {
    blockedIps: BlockedIpEntry[];
    onChange: (list: BlockedIpEntry[]) => void;
}) {
    const [ipInput, setIpInput] = useState('');
    const [noteInput, setNoteInput] = useState('');
    const [adding, setAdding] = useState(false);
    const [inputError, setInputError] = useState('');

    const isValidIp = (ip: string) => {
        const v4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(ip.trim());
        const v6 = ip.trim().includes(':') && ip.trim().length > 2;
        return v4 || v6;
    };

    const handleAdd = async () => {
        const raw = ipInput.trim();
        if (!raw) return;
        if (!isValidIp(raw)) { setInputError('IP không hợp lệ. Ví dụ: 192.168.1.100'); return; }
        if (blockedIps.some(e => e.label === raw)) { setInputError('IP này đã có trong danh sách.'); return; }
        setAdding(true);
        setInputError('');
        try {
            const hash = await sha256(raw);
            onChange([...blockedIps, {
                label: raw,
                hash,
                note: noteInput.trim() || undefined,
                added_at: new Date().toISOString(),
            }]);
            setIpInput('');
            setNoteInput('');
        } finally {
            setAdding(false);
        }
    };

    return (
        <div className="mt-5 border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🚫</span>
                <span className="text-sm font-semibold text-gray-800">Chặn IP cụ thể</span>
                <span className="text-xs text-gray-500">— chặn vĩnh viễn, không phụ thuộc giới hạn số lượng</span>
                {blockedIps.length > 0 && (
                    <span className="ml-auto text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                        {blockedIps.length} IP bị chặn
                    </span>
                )}
            </div>
            <div className="flex gap-2 flex-wrap">
                <input
                    type="text"
                    placeholder="Nhập địa chỉ IP (ví dụ: 1.2.3.4)"
                    value={ipInput}
                    onChange={e => { setIpInput(e.target.value); setInputError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    className="flex-1 min-w-36 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-red-400 focus:ring-1 focus:ring-red-300 focus:outline-none"
                />
                <input
                    type="text"
                    placeholder="Ghi chú (tuỳ chọn)"
                    value={noteInput}
                    onChange={e => setNoteInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    className="flex-1 min-w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-400 focus:outline-none"
                />
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={adding || !ipInput.trim()}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40 shrink-0"
                >
                    {adding ? '…' : '+ Chặn IP'}
                </button>
            </div>
            {inputError && <p className="mt-1 text-xs text-red-600">⚠ {inputError}</p>}

            {blockedIps.length > 0 ? (
                <div className="mt-3 space-y-1.5 max-h-52 overflow-y-auto pr-1">
                    {blockedIps.map((entry) => (
                        <div key={entry.hash} className="flex items-center justify-between gap-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="text-sm font-mono font-bold text-red-800 shrink-0">🚫 {entry.label}</span>
                                <code className="text-[10px] text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono" title={entry.hash}>
                                    {entry.hash.slice(0, 12)}…
                                </code>
                                {entry.note && <span className="text-xs text-gray-500 truncate">{entry.note}</span>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {entry.added_at && (
                                    <span className="text-[10px] text-gray-400 hidden sm:inline">
                                        {new Date(entry.added_at).toLocaleDateString('vi-VN')}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onChange(blockedIps.filter(e => e.hash !== entry.hash))}
                                    className="text-xs text-red-400 hover:text-red-700 hover:bg-red-100 rounded px-1.5 py-0.5 transition-colors"
                                    title="Bỏ chặn IP này"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="mt-3 text-xs text-gray-400 italic">Chưa có IP nào bị chặn thủ công.</p>
            )}
        </div>
    );
}

export default function CampaignEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';
    const isSuperAdmin = !!user?.roles?.includes('super_admin');
    const { hasFeature, entitlements, hasGameType, refresh: refreshEntitlements } = useEntitlements(id);
    const isCustomFormAllowed = hasFeature('custom_form');
    const isWebhookAllowed = hasFeature('webhook');
    const isIpBlockAllowed = isSuperAdmin || hasFeature('ip_block');
    const isRemoveWatermarkAllowed = hasFeature('remove_watermark');

    const [campaign, setCampaign] = useState<Campaign>(emptyCampaign);
    const [isDirty, setIsDirty] = useState(false); // track unsaved changes
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<TabId>(id ? 'overview' : 'settings');
    const [gameInstances, setGameInstances] = useState<GameInstance[]>([]);
    const [gameTemplates, setGameTemplates] = useState<GameTemplate[]>([]);
    const [gifts, setGifts] = useState<Gift[]>([]);
    const [players, setPlayers] = useState<Player[]>([]);
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [showAddGameModal, setShowAddGameModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [editingGame, setEditingGame] = useState<GameInstance | null>(null);
    const [uploading, setUploading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [gameName, setGameName] = useState('');

    const [lockedFeatureInfo, setLockedFeatureInfo] = useState<{ name: string; requiredXu: number; requiredLevel: string } | null>(null);
    const [walletBalance, setWalletBalance] = useState<number | null>(null);
    const [plans, setPlans] = useState<any[]>([]);

    const getDaysDiff = (start?: string, end?: string) => {
        if (!start || !end) return 0;
        const s = new Date(start);
        const e = new Date(end);
        const diffTime = Math.abs(e.getTime() - s.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const openLockNotice = async (featureName: string, requiredXu: number, requiredLevel: string) => {
        setLockedFeatureInfo({ name: featureName, requiredXu, requiredLevel });
        try {
            const walletRes = await api.getWallet();
            setWalletBalance(walletRes.data?.balance ?? 0);
        } catch (err) {
            console.error('Failed to load wallet balance', err);
        }
    };

    const firstGame = gameInstances[0];
    const totals = useMemo(() => gifts.reduce((acc, gift) => {
        acc.imported += gift.total_quantity || 0;
        acc.remaining += getRemaining(gift);
        acc.assigned += gift.assigned_quantity || 0;
        acc.claimed += gift.claimed_quantity || 0;
        return acc;
    }, { imported: 0, remaining: 0, assigned: 0, claimed: 0 }), [gifts]);

    const loadWorkspace = useCallback(async () => {
        // Luôn fetch số dư ví khi load trang
        try {
            const walletRes = await api.getWallet().catch(() => null);
            if (walletRes) {
                setWalletBalance(walletRes.data?.balance ?? 0);
            }
        } catch (err) {
            console.error('Failed to fetch wallet:', err);
        }

        if (!id) {
            setLoading(false);
            return;
        }

        try {
            const [campaignData, instances, templates, giftData, playerData, metricData] = await Promise.all([
                api.getCampaign(id),
                api.getGameInstancesByCampaign(id).catch(() => []),
                api.getGameTemplates(1, 100).catch(() => ({ data: [] })),
                api.getGifts(tenantId, id, 1, 100).catch(() => ({ data: [] })),
                api.searchPlayers(tenantId, id, undefined, 1, 20).catch(() => ({ data: [] })),
                api.getCampaignMetrics(id, tenantId).catch(() => null),
            ]);

            // Load campaign data lần đầu — luôn ghi đè
            setCampaign({
                ...campaignData,
                start_at: campaignData.start_at ? new Date(campaignData.start_at).toISOString().slice(0, 10) : '',
                end_at: campaignData.end_at ? new Date(campaignData.end_at).toISOString().slice(0, 10) : '',
            });
            setIsDirty(false);
            setGameInstances(instances || []);
            setGameTemplates(parsePaginatedResponse<GameTemplate>(templates).items);
            setGifts(parsePaginatedResponse<Gift>(giftData).items);
            setPlayers(parsePaginatedResponse<Player>(playerData).items);
            setMetrics(metricData);
        } catch (err) {
            console.error('Failed to load campaign workspace:', err);
        } finally {
            setLoading(false);
        }
    }, [id, tenantId]);

    // Chỉ refresh gifts/players/metrics — KHÔNG ghi đè campaign settings
    const refreshData = useCallback(async () => {
        if (!id) return;
        try {
            const [instances, giftData, playerData, metricData, walletRes] = await Promise.all([
                api.getGameInstancesByCampaign(id).catch(() => []),
                api.getGifts(tenantId, id, 1, 100).catch(() => ({ data: [] })),
                api.searchPlayers(tenantId, id, undefined, 1, 20).catch(() => ({ data: [] })),
                api.getCampaignMetrics(id, tenantId).catch(() => null),
                api.getWallet().catch(() => null),
            ]);
            setGameInstances(instances || []);
            setGifts(parsePaginatedResponse<Gift>(giftData).items);
            setPlayers(parsePaginatedResponse<Player>(playerData).items);
            setMetrics(metricData);
            if (walletRes) {
                setWalletBalance(walletRes.data?.balance ?? 0);
            }
        } catch (err) {
            console.error('Failed to refresh workspace data:', err);
        }
    }, [id, tenantId]);

    useEffect(() => {
        loadWorkspace();
    }, [loadWorkspace]);

    // Auto-refresh chỉ gọi refreshData (không đụng campaign settings)
    useEffect(() => {
        if (!id) return;
        const interval = setInterval(() => {
            refreshData();
        }, WORKSPACE_POLL_MS);
        return () => clearInterval(interval);
    }, [id, refreshData]);

    useEffect(() => {
        if (!id) return;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshData(); // Chỉ refresh data, không đụng form
            }
        };

        window.addEventListener('focus', refreshData);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', refreshData);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [id, refreshData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...campaign,
                tenant_id: tenantId,
                start_at: campaign.start_at ? new Date(campaign.start_at).toISOString() : null,
                end_at: campaign.end_at ? new Date(campaign.end_at).toISOString() : null,
            };
            if (id) {
                await api.updateCampaign(id, payload);
                setIsDirty(false);
                await loadWorkspace(); // Sau khi save xong mới reload campaign
                setActiveTab('overview');
            } else {
                await api.createCampaign(payload);
                navigate('/campaigns');
            }
        } catch (err: any) {
            console.error('Failed to save campaign:', err);
            const errData = err.response?.data || {};
            if (errData.code === 'PLAN_CAMPAIGN_DURATION_LIMIT') {
                alert(`⚠️ Giới hạn thời lượng chạy:\n\nChiến dịch dùng thử (Free) chỉ được phép chạy tối đa 3 ngày.\n\nĐể chạy không giới hạn thời gian, vui lòng nạp xu và kích hoạt bất kỳ tính năng trả phí nào (ví dụ: bật "Ẩn logo BanhXeSo" hoặc sử dụng "Biểu mẫu nâng cao"). Hệ thống sẽ tự động trừ xu hàng ngày (Pay-per-day) khi chiến dịch hoạt động.`);
                return;
            }
            const msg = errData.message || err.message || 'Không thể lưu cấu hình chiến dịch. Vui lòng kiểm tra lại.';
            alert(`⚠️ Lỗi: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (status: CampaignStatus) => {
        if (!id) return;
        try {
            await api.updateCampaignStatus(id, status);
            await loadWorkspace();
        } catch (err: any) {
            console.error('Failed to update status:', err);
            const msg = err.response?.data?.message || err.message || 'Hãy kiểm tra game, rule quà, quota gói và thử lại.';
            alert(`⚠️ Lỗi: Không thể cập nhật trạng thái chiến dịch. ${msg}`);
        }
    };

    const handleAddGame = async () => {
        if (!id || !selectedTemplate || !gameName) return;
        try {
            await api.createGameInstanceFromTemplate(selectedTemplate, id, {
                name: gameName,
                config: {},
            });
            await loadWorkspace();
            setShowAddGameModal(false);
            setSelectedTemplate('');
            setGameName('');
        } catch (err: any) {
            console.error('Failed to add game:', err);
            alert('Không thể thêm trò chơi: ' + (err?.message || 'Lỗi không xác định'));
        }
    };

    const handleUploadGame = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!id) return;
        const formData = new FormData(e.currentTarget);
        setUploading(true);
        try {
            const result = await api.uploadGameZip(id, formData);
            if (result.success) {
                await loadWorkspace();
                setShowUploadModal(false);
            } else {
                alert(result.message || 'Upload failed');
            }
        } catch (err) {
            console.error('Failed to upload game:', err);
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteGame = async (gameId: string) => {
        if (!confirm('Delete this game instance?')) return;
        try {
            await api.deleteGameInstance(gameId);
            setGameInstances(prev => prev.filter(g => g.id !== gameId));
        } catch (err) {
            console.error('Failed to delete game:', err);
        }
    };

    const handleToggleGameStatus = async (gameId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            await api.updateGameInstanceStatus(gameId, newStatus);
            setGameInstances(prev => prev.map(g => g.id === gameId ? { ...g, status: newStatus } : g));
        } catch (err) {
            console.error('Failed to update game status:', err);
        }
    };

    const tabs: { id: TabId; label: string }[] = [
        { id: 'overview',  label: 'Tổng quan' },
        { id: 'games' as TabId, label: 'Trò chơi' },
        { id: 'gifts',     label: 'Quà tặng' },
        { id: 'players',   label: 'Người chơi' },
        { id: 'forms',     label: `📋 Biểu mẫu ${isCustomFormAllowed ? '' : '🔒'}` },
        { id: 'analytics', label: 'Thống kê' },
        { id: 'settings',  label: 'Cài đặt' },
    ];

    if (loading) {
        return (
            <Layout>
                <div className="flex h-64 items-center justify-center text-gray-500">Đang tải chiến dịch...</div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-3xl font-bold text-gray-900">{id ? campaign.name : 'Chiến dịch mới'}</h1>
                        {id && campaign.status && (
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColors[campaign.status] || 'bg-gray-100 text-gray-800'}`}>
                                {getStatusLabel(campaign.status)}
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                        {id ? `${campaign.slug} — ${formatDate(campaign.start_at)} đến ${formatDate(campaign.end_at)}` : 'Tạo chiến dịch, sau đó gắn trò chơi, quà tặng và dữ liệu báo cáo.'}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-bold text-white bg-indigo-600`}>
                            💎 {entitlements?.plan_label || 'Đang tải...'}
                        </span>
                        <span className="font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">
                            👛 Ví xu: {walletBalance !== null ? `${walletBalance} xu` : '...'}
                        </span>
                        {id && entitlements?.daily_rate !== undefined && (
                            <span className="font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                                ⚡ Phí ngày chạy: {entitlements.daily_rate} xu/ngày
                            </span>
                        )}
                    </div>
                    {campaign.description && <p className="mt-2 max-w-3xl text-sm text-gray-600">{campaign.description}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/campaigns" className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50">Quay lại</Link>
                    {firstGame && (
                        <button onClick={() => window.open(`/preview/instance/${firstGame.id}`, '_blank')} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800">
                            {campaign.status === 'active' ? 'Xem trước' : 'Xem nháp'}
                        </button>
                    )}
                    {id && campaign.status === 'draft' && (
                        <button onClick={() => handleStatusChange('active')} className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700">
                            Phát hành
                        </button>
                    )}
                    {id && campaign.status === 'active' && (
                        <button onClick={() => handleStatusChange('paused')} className="rounded-lg bg-yellow-600 px-4 py-2 text-sm text-white hover:bg-yellow-700">
                            Tạm dừng
                        </button>
                    )}
                    {id && campaign.status && !['draft', 'active'].includes(campaign.status) && (
                        <CampaignStatusActions status={campaign.status} onChange={handleStatusChange} />
                    )}
                </div>
            </div>

            {id && (
                <>
                    {/* ── QR & Public Link Panel ── */}
                    {firstGame && (
                        <SharePanel 
                            instanceId={firstGame.id} 
                            campaignName={campaign.name} 
                            isDraft={campaign.status !== 'active'} 
                        />
                    )}

                    {!firstGame && (
                        <div className="mb-5 overflow-hidden rounded-xl border border-dashed border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5 shadow-sm">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-start gap-3">
                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-amber-900 animate-pulse">Chiến dịch chưa được gắn trò chơi</h3>
                                        <p className="mt-1 text-sm text-amber-700">
                                            Để có Link công khai và mã QR Code cho người chơi tham gia, bạn cần gắn một trò chơi vào chiến dịch này.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveTab('games');
                                        setShowAddGameModal(true);
                                    }}
                                    className="rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors whitespace-nowrap self-start sm:self-center"
                                >
                                    🎮 Gắn trò chơi ngay
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <SummaryCard label="Người chơi" value={metricValue(metrics?.total_players || players.length)} detail={`${metricValue(metrics?.unique_players)} duy nhất`} tone="text-blue-600" />
                        <SummaryCard label="Lượt chơi" value={metricValue(metrics?.total_plays || metrics?.total_sessions)} detail={`${metricValue(metrics?.conversion_rate, '%')} chuyển đổi`} tone="text-teal-600" />
                        <SummaryCard label="Quà tặng" value={metricValue(gifts.length)} detail={`${metricValue(totals.remaining)} còn lại`} tone="text-green-600" />
                        <SummaryCard label="Phần thưởng" value={metricValue(metrics?.total_rewards_awarded || totals.assigned)} detail={`${metricValue(totals.claimed)} đã nhận`} tone="text-orange-600" />
                    </div>

                    <div className="mb-6 border-b border-gray-200">
                        <nav className="flex flex-wrap gap-2">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-800'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </>
            )}

            {id && activeTab === 'overview' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900">Tình trạng chiến dịch</h2>
                            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                                <div>
                                    <div className="text-xs text-gray-500">Trạng thái public</div>
                                    <div className="mt-1 font-semibold text-gray-900">{getStatusLabel(campaign.status)}</div>
                                    <div className="text-xs text-gray-500">{campaign.status === 'active' ? 'Đang nhận lượt chơi thật' : 'Public play đang bị khóa'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Trò chơi</div>
                                    <div className="mt-1 font-semibold text-gray-900">{firstGame ? getGameLabel(firstGame.game_template) : 'Chưa gắn trò chơi'}</div>
                                    <div className="text-xs text-gray-500">{gameInstances.length} phiên bản</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Kho quà</div>
                                    <div className="mt-1 font-semibold text-gray-900">{metricValue(totals.imported)} đã nhập</div>
                                    <div className="text-xs text-gray-500">{metricValue(totals.assigned)} đã phát / {metricValue(totals.claimed)} đã nhận</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Form thu thập</div>
                                    <div className="mt-1 font-semibold text-gray-900">{campaign.require_form_before_play ? 'Trước khi chơi' : 'Sau khi trúng / tùy chọn'}</div>
                                    <div className="text-xs text-gray-500">{campaign.play_cooldown_minutes ? `Chờ ${campaign.play_cooldown_minutes} phút giữa các lượt` : 'Không giới hạn thời gian'}</div>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900">Thao tác nhanh</h2>
                            <div className="mt-4 grid gap-2">
                                <button onClick={() => setActiveTab('games')} className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50">Quản lý trò chơi</button>
                                <button onClick={() => setActiveTab('gifts')} className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50">Xem kho quà tặng</button>
                                <button onClick={() => setActiveTab('players')} className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50">Xem người chơi</button>
                                <button onClick={() => setActiveTab('settings')} className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50">Chỉnh sửa cài đặt</button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <GiftTable gifts={gifts.slice(0, 5)} compact />
                        <PlayerTable players={players.slice(0, 5)} compact />
                    </div>
                </div>
            )}

            {id && activeTab === 'games' && (
                <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Trò chơi</h2>
                            <p className="text-sm text-gray-500">Gắn, xem trước và quản lý các phiên bản trò chơi cho chiến dịch này.</p>
                        </div>
                        <div className="flex gap-2">
                            {isSuperAdmin && (
                                <button onClick={() => setShowUploadModal(true)} className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700">Tải ZIP lên</button>
                            )}
                            <button 
                                onClick={() => {
                                    if (gameInstances.length > 0) {
                                        alert("Mỗi chiến dịch chỉ được phép có tối đa 1 trò chơi. Vui lòng xóa trò chơi hiện tại trước khi thêm trò chơi mới.");
                                        return;
                                    }
                                    setShowAddGameModal(true);
                                }} 
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
                            >
                                Thêm trò chơi
                            </button>
                        </div>
                    </div>

                    {gameInstances.length === 0 ? (
                        <EmptyPanel title="Chưa gắn trò chơi" description="Thêm mẫu trò chơi hoặc tải file ZIP lên trước khi phát hành chiến dịch." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <Th>Trò chơi</Th>
                                        <Th>Mẫu</Th>
                                        <Th>Trạng thái</Th>
                                        <Th>Ngày tạo</Th>
                                        <Th>Hành động</Th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {gameInstances.map((game) => (
                                        <tr key={game.id} className="hover:bg-gray-50">
                                            <Td strong>{game.name}</Td>
                                            <Td>{getGameLabel(game.game_template)} <span className="text-gray-400">({getGameTypeCode(game.game_template)})</span></Td>
                                            <Td>
                                                <button onClick={() => handleToggleGameStatus(game.id, game.status)} className={`rounded-full px-2 py-1 text-xs font-semibold ${game.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                    {game.status}
                                                </button>
                                            </Td>
                                            <Td>{formatDate(game.created_at)}</Td>
                                            <Td>
                                                <button onClick={() => setEditingGame(game)} className="mr-3 text-emerald-600 hover:text-emerald-800 font-medium">Cấu hình</button>
                                                <button onClick={() => window.open(`/preview/instance/${game.id}`, '_blank')} className="mr-3 text-blue-600 hover:text-blue-800">Xem trước</button>
                                                <button onClick={() => handleDeleteGame(game.id)} className="text-red-600 hover:text-red-800">Xóa</button>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {id && activeTab === 'gifts' && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Quà tặng trong chiến dịch</h2>
                            <p className="text-sm text-gray-500">Tồn kho, phân bổ, nhận quà, hết hạn và trạng thái kho trong phạm vi chiến dịch này.</p>
                        </div>
                        <Link to={`/gifts?campaignId=${id}`} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">Quản lý quà tặng</Link>
                    </div>
                    <GiftTable gifts={gifts} />
                </div>
            )}

            {id && activeTab === 'players' && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Người chơi & Dữ liệu lead</h2>
                            <p className="text-sm text-gray-500">Danh sách dữ liệu người chơi được thu thập bởi chiến dịch này.</p>
                        </div>
                        <Link to={`/players?campaignId=${id}`} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">Quản lý người chơi</Link>
                    </div>
                    <PlayerTable players={players} />
                </div>
            )}

            {id && activeTab === 'analytics' && (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
                        <SummaryCard label="Phiên chơi" value={metricValue(metrics?.total_sessions)} detail="Tổng lượt bắt đầu" />
                        <SummaryCard label="Lượt hoàn thành" value={metricValue(metrics?.total_plays)} detail="Status: finished" tone="text-teal-600" />
                        <SummaryCard label="Phần thưởng đã trao" value={metricValue(metrics?.total_rewards_awarded)} tone="text-orange-600" />
                        <SummaryCard label="Form đã gửi" value={metricValue(metrics?.total_forms_submitted)} detail="Có thông tin khách" tone="text-blue-600" />
                        <SummaryCard label="Tỷ lệ thắng" value={metricValue(metrics?.win_rate, '%')} detail={`Trên ${metricValue(metrics?.total_plays)} lượt`} tone="text-purple-600" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <MetricMap title="Phiên theo trạng thái" values={metrics?.sessions_by_status} />
                        <MetricMap title="Phần thưởng theo kết quả" values={metrics?.rewards_by_type} />
                    </div>
                    <MetricMap title="Lượt chơi theo ngày" values={metrics?.plays_by_day} wide />
                </div>
            )}

            {id && activeTab === 'forms' && (
                isCustomFormAllowed ? (
                    <FormSubmissionsTab campaignId={id} onAfterClaim={refreshData} />
                ) : (
                    <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-orange-50 text-5xl">
                            🔒
                        </div>
                        <h3 className="mt-6 text-xl font-bold text-gray-900">Tính năng Thu Thập Thông Tin Khách Hàng bị khóa</h3>
                        <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500 leading-relaxed">
                            Biểu mẫu tuỳ chỉnh (Custom Lead Form) là tính năng nâng cao giúp bạn thu thập Họ tên, Số điện thoại, Email... của người chơi trước khi quay hoặc sau khi trúng thưởng. Tính năng này yêu cầu nâng cấp lên gói cước hạng <strong>Mini</strong> hoặc cao hơn.
                        </p>
                        <div className="mt-8 flex justify-center gap-4">
                            <Link
                                to="/billing"
                                className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-2.5 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all uppercase tracking-wide"
                            >
                                👑 Nâng cấp gói cước ngay
                            </Link>
                        </div>
                    </div>
                )
            )}

            {(!id || activeTab === 'settings') && (
                <div className="mx-auto w-full max-w-[600px]">
                    <form onSubmit={handleSubmit} className="mb-8 space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    {id && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Trạng thái vận hành</div>
                                    <div className="mt-1 text-lg font-semibold text-gray-900">{getStatusLabel(campaign.status)}</div>
                                    <p className="mt-1 text-sm text-gray-600">{getStatusDescription(campaign.status)}</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <CampaignStatusActions status={campaign.status} onChange={handleStatusChange} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <Field label="Tên chiến dịch">
                            <input type="text" value={campaign.name} onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))} className={inputClass} required />
                        </Field>
                        <Field label="Slug">
                            <input type="text" value={campaign.slug} onChange={(e) => setCampaign(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} className={inputClass} required />
                        </Field>
                    </div>
                    <Field label="Mô tả">
                        <textarea value={campaign.description || ''} onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))} rows={3} className={inputClass} />
                    </Field>
                    <Field label="📖 Hướng dẫn chơi game (Hiển thị trong game)">
                        <textarea
                            value={campaign.public_config?.game_instructions || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCampaign(prev => ({
                                    ...prev,
                                    public_config: {
                                        ...(prev.public_config || {}),
                                        game_instructions: val
                                    }
                                }));
                                setIsDirty(true);
                            }}
                            rows={4}
                            className={inputClass}
                            placeholder={`Ví dụ:
1. Quét tay/chuột trên lớp bạc phủ để cào thẻ.
2. Cào sạch tối thiểu 80% diện tích thẻ.
3. Nhận phần quà may mắn tương ứng.`}
                        />
                        <p className="text-xs text-gray-400 mt-1">Mỗi dòng sẽ tự động hiển thị thành một bước hướng dẫn (1, 2, 3...) khi người chơi bắt đầu game.</p>
                        
                        <div className={`border p-4 rounded-xl transition-colors mt-3 ${
                            campaign.public_config?.show_tutorial_on_start !== false
                                ? 'border-indigo-400 bg-indigo-50/30'
                                : 'border-gray-200 bg-gray-50'
                        }`}>
                            <div className="flex items-start gap-3">
                                <div className="mt-0.5">
                                    <input
                                        id="show_tutorial_on_start"
                                        type="checkbox"
                                        checked={campaign.public_config?.show_tutorial_on_start !== false}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setCampaign(prev => ({
                                                ...prev,
                                                public_config: {
                                                    ...(prev.public_config || {}),
                                                    show_tutorial_on_start: checked
                                                }
                                            }));
                                            setIsDirty(true);
                                        }}
                                        className="w-4 h-4 accent-indigo-500 cursor-pointer"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="show_tutorial_on_start" className="font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                                        💡 Tự động mở hướng dẫn chơi khi bắt đầu
                                        {campaign.public_config?.show_tutorial_on_start !== false && (
                                            <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">Đang bật</span>
                                        )}
                                    </label>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Tự động hiển thị popup <strong>❓ Hướng Dẫn Chơi</strong> khi khách hàng vào game lần đầu tiên trong phiên.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Field>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <Field label="Ngày bắt đầu">
                            <input type="date" value={campaign.start_at || ''} onChange={(e) => setCampaign(prev => ({ ...prev, start_at: e.target.value }))} className={inputClass} />
                        </Field>
                        <Field label="Ngày kết thúc">
                            <input type="date" value={campaign.end_at || ''} onChange={(e) => setCampaign(prev => ({ ...prev, end_at: e.target.value }))} className={inputClass} />
                        </Field>
                    </div>

                    {/* Tỷ lệ trúng thưởng tổng */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">🎯 Tỷ lệ trúng thưởng tổng</label>
                            <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                                (campaign.win_rate ?? 30) >= 60 ? 'bg-red-100 text-red-700'
                                : (campaign.win_rate ?? 30) >= 30 ? 'bg-amber-100 text-amber-700'
                                : 'bg-green-100 text-green-700'
                            }`}>{campaign.win_rate ?? 30}%</span>
                        </div>
                        <input
                            type="range" min={0} max={100} step={1}
                            value={campaign.win_rate ?? 30}
                            onChange={(e) => setCampaign(prev => ({ ...prev, win_rate: parseInt(e.target.value, 10) }))}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-600"
                            style={{ background: `linear-gradient(to right, ${
                                (campaign.win_rate ?? 30) >= 60 ? '#ef4444'
                                : (campaign.win_rate ?? 30) >= 30 ? '#f59e0b' : '#22c55e'
                            } ${campaign.win_rate ?? 30}%, #e5e7eb ${campaign.win_rate ?? 30}%)` }}
                        />
                        <div className="flex justify-between text-xs text-gray-400">
                            <span>0% — Không ai trúng</span>
                            <span className="text-gray-500">Thua: {100 - (campaign.win_rate ?? 30)}%</span>
                            <span>100% — Ai cũng trúng</span>
                        </div>
                        <p className="text-xs text-gray-400">Engine sẽ roll 1 lần duy nhất. Nếu trúng → chọn quà theo trọng số. Nếu thua → hiện "Chúc may mắn lần sau".</p>
                    </div>

                    {/* Cooldown giữa các lượt */}
                    <Field label="Thời gian chờ giữa 2 lượt chơi">
                        <div className="flex gap-2 items-center flex-wrap">
                            {/* Hằng số presets để tránh lặp */}
                            {(() => {
                                const PRESETS = ['30','60','240','480','1440'];
                                const cur = campaign.play_cooldown_minutes;
                                // Xác định select đang hiện option nào
                                const selectVal = cur == null
                                    ? 'none'
                                    : PRESETS.includes(String(cur))
                                        ? String(cur)
                                        : 'custom';

                                return (
                                    <>
                                        <select
                                            className={inputClass}
                                            value={selectVal}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === 'none') {
                                                    setCampaign(prev => ({ ...prev, play_cooldown_minutes: null }));
                                                } else if (v === 'custom') {
                                                    // Dùng -1 làm flag "custom chưa nhập"
                                                    setCampaign(prev => ({ ...prev, play_cooldown_minutes: -1 }));
                                                } else {
                                                    setCampaign(prev => ({ ...prev, play_cooldown_minutes: parseInt(v, 10) }));
                                                }
                                            }}
                                        >
                                            <option value="none">Không giới hạn thời gian</option>
                                            <option value="30">30 phút</option>
                                            <option value="60">1 giờ</option>
                                            <option value="240">4 giờ</option>
                                            <option value="480">8 giờ</option>
                                            <option value="1440">24 giờ (1 lần/ngày)</option>
                                            <option value="custom">Tùy chỉnh...</option>
                                        </select>

                                        {/* Hiện input khi đang ở chế độ custom */}
                                        {selectVal === 'custom' && (
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    min={1}
                                                    autoFocus
                                                    placeholder="Nhập số phút"
                                                    value={cur != null && cur > 0 ? cur : ''}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        setCampaign(prev => ({
                                                            ...prev,
                                                            play_cooldown_minutes: isNaN(val) || val < 1 ? -1 : val,
                                                        }));
                                                    }}
                                                    className={`${inputClass} w-32`}
                                                />
                                                <span className="text-sm text-gray-500 whitespace-nowrap">phút</span>
                                                {/* Preview giá trị đã nhập */}
                                                {cur != null && cur > 0 && (
                                                    <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full whitespace-nowrap">
                                                        {cur >= 60
                                                            ? `≈ ${(cur / 60).toFixed(1)} giờ`
                                                            : `${cur} phút`}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Sau khi quay, player phải đợi X phút mới được quay lần tiếp theo.</p>
                    </Field>

                    {/* Kiosk Mode */}
                    <div className={`rounded-xl border-2 p-4 transition-all ${
                        campaign.kiosk_mode
                            ? 'border-emerald-400 bg-emerald-50'
                            : 'border-gray-200 bg-gray-50'
                    }`}>
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                                <input
                                    id="kiosk_mode"
                                    type="checkbox"
                                    checked={campaign.kiosk_mode || false}
                                    onChange={(e) => setCampaign(prev => ({ ...prev, kiosk_mode: e.target.checked }))}
                                    className="w-4 h-4 accent-emerald-500 cursor-pointer"
                                />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="kiosk_mode" className="font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                                    📱 Chế độ Kiosk (Shop Mode)
                                    {campaign.kiosk_mode && (
                                        <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Đang bật</span>
                                    )}
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    Dành cho shop dùng <strong>1 iPad/điện thoại</strong> để quay cho nhiều khách liên tiếp.
                                </p>
                                <ul className="text-xs text-gray-500 mt-2 space-y-1 list-none">
                                    <li>✅ Giới hạn lượt theo <strong>số điện thoại</strong></li>
                                    <li>✅ Tự động reset màn hình sau khi quay</li>
                                    <li>✅ <strong>Bỏ qua giới hạn IP</strong> — nhiều khách cùng dùng 1 thiết bị không bị chặn</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Ẩn logo BanhXeSo */}
                    <div className={`rounded-xl border-2 p-4 transition-all ${
                        campaign.remove_watermark
                            ? 'border-indigo-400 bg-indigo-50/30'
                            : 'border-gray-200 bg-gray-50'
                    }`}>
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                                <input
                                    id="remove_watermark"
                                    type="checkbox"
                                    checked={campaign.remove_watermark || false}
                                    onChange={(e) => {
                                        if (!isRemoveWatermarkAllowed && e.target.checked) {
                                            openLockNotice('Ẩn logo BanhXeSo', 100, 'Hạng Bạc');
                                            return;
                                        }
                                        setCampaign(prev => ({ ...prev, remove_watermark: e.target.checked }));
                                        setIsDirty(true);
                                    }}
                                    className="w-4 h-4 accent-indigo-500 cursor-pointer"
                                />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="remove_watermark" className="font-semibold text-gray-900 cursor-pointer flex items-center gap-2">
                                    🚫 Ẩn logo BanhXeSo
                                    {campaign.remove_watermark && (
                                        <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">Đang ẩn</span>
                                    )}
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    Loại bỏ logo thương hiệu BanhXeSo hiển thị ở góc màn hình game của người chơi.
                                </p>
                                {!isRemoveWatermarkAllowed && (
                                    <div className="mt-2 text-xs text-amber-600 font-semibold flex items-center gap-1">
                                        🔒 Yêu cầu Hạng Bạc trở lên (ví ≥ 100 xu, phí 15 xu/ngày khi chạy).
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Thời điểm hiển thị form — radio group (mutually exclusive) */}
                    <div className="relative rounded-2xl border border-gray-150 p-5 bg-gray-50/50">
                        {/* Lock Overlay */}
                        {!isCustomFormAllowed && (
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl bg-gray-100/80 backdrop-blur-[0.5px] p-6 text-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-2xl shadow-sm animate-pulse">
                                    🔒
                                </div>
                                <h4 className="mt-3 font-bold text-gray-900 text-sm">Biểu Mẫu thu thập thông tin bị khóa</h4>
                                <p className="mt-1 max-w-md text-xs text-gray-500 leading-relaxed font-semibold">
                                    Tài khoản hiện tại ({entitlements?.plan_label || 'Dùng thử'}) chưa đủ điều kiện sử dụng Biểu mẫu nâng cao. Yêu cầu Hạng Bạc trở lên (ví ≥ 100 xu, phí 15 xu/ngày khi chạy).
                                </p>
                                <button
                                    type="button"
                                    onClick={() => openLockNotice('Biểu mẫu thu thập nâng cao', 100, 'Hạng Bạc')}
                                    className="mt-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 px-5 py-2.5 text-[11px] font-bold text-white shadow-md hover:shadow-lg transition-all uppercase tracking-wide flex items-center gap-1.5 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                                >
                                    💎 Mở khóa tính năng
                                </button>
                            </div>
                        )}

                        {/* Thời điểm hiển thị form — radio group */}
                        <div className={`space-y-2 ${!isCustomFormAllowed ? 'pointer-events-none select-none blur-[0.5px]' : ''}`}>
                            <div className="text-sm font-medium text-gray-700">Thu thập thông tin người chơi</div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                                {[
                                    { value: 'none',   label: '🚫 Không dùng form',        desc: 'Không yêu cầu điền thông tin' },
                                    { value: 'before', label: '📋 Form trước khi chơi',    desc: 'Điền tên, SĐT... trước khi quay' },
                                    { value: 'after',  label: '🎁 Form sau khi trúng',     desc: 'Điền thông tin để nhận quà' },
                                ].map(({ value, label, desc }) => {
                                    const current = !isCustomFormAllowed
                                        ? 'none'
                                        : campaign.require_form_before_play
                                            ? 'before'
                                            : campaign.require_form_after_win !== false
                                                ? 'after'
                                                : 'none';
                                    const checked = current === value;
                                    return (
                                        <label
                                            key={value}
                                            className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-all ${
                                                checked
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="form_timing"
                                                value={value}
                                                checked={checked}
                                                disabled={!isCustomFormAllowed}
                                                onChange={() => setCampaign(prev => ({
                                                    ...prev,
                                                    require_form_before_play: value === 'before',
                                                    require_form_after_win: value === 'after',
                                                }))}
                                                className="mt-0.5 accent-blue-600"
                                            />
                                            <div>
                                                <div className={`text-sm font-semibold ${checked ? 'text-blue-700' : 'text-gray-800'}`}>{label}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Form Fields Editor — show when any form is enabled */}
                        {isCustomFormAllowed && (campaign.require_form_before_play || campaign.require_form_after_win) && (
                            <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/30 p-5">
                                <div className="mb-3">
                                    <h3 className="font-semibold text-gray-900">📋 Cấu hình trường form</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {campaign.require_form_before_play && campaign.require_form_after_win
                                            ? 'Form sẽ hiển thị trước khi chơi VÀ sau khi trúng thưởng'
                                            : campaign.require_form_before_play
                                                ? 'Form sẽ hiển thị TRƯỚC khi quay'
                                                : 'Form sẽ hiển thị SAU KHI TRÚNG thưởng'}
                                    </p>
                                </div>
                                <FormFieldsEditor
                                    fields={campaign.form_fields || []}
                                    onChange={(fields) => {
                                        setCampaign(prev => ({ ...prev, form_fields: fields }));
                                        setIsDirty(true);
                                    }}
                                    themeId={gameInstances[0]?.config?.theme || 'blue'}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* 🛡️ Anti-Fraud IP Limit — chỉ Super Admin thấy */}
                    {isSuperAdmin && (
                        <div className={`rounded-2xl border-2 p-5 transition-all ${
                            campaign.private_config?.anti_fraud?.ip_limit_enabled !== false
                                ? 'border-red-300 bg-red-50/40'
                                : 'border-gray-200 bg-gray-50/50'
                        }`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl mt-0.5">🛡️</span>
                                    <div>
                                        <div className="font-semibold text-gray-900 flex items-center gap-2">
                                            Giới hạn IP — Anti-Fraud
                                            {campaign.private_config?.anti_fraud?.ip_limit_enabled !== false ? (
                                                <span className="text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">Đang bật</span>
                                            ) : (
                                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Đã tắt</span>
                                            )}
                                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full font-medium">Super Admin only</span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                        <strong>Mặc định luôn bật:</strong> Tối đa 15 lượt chơi / IP / ngày. Ngăn chặn 1 người hoặc bot farm chơi quá nhiều lần từ cùng địa chỉ mạng.
                                        </p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                    <input
                                        type="checkbox"
                                        checked={campaign.private_config?.anti_fraud?.ip_limit_enabled !== false}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setCampaign(prev => ({
                                                ...prev,
                                                private_config: {
                                                    ...(prev.private_config || {}),
                                                    anti_fraud: {
                                                        ...(prev.private_config?.anti_fraud || {}),
                                                        ip_limit_enabled: enabled,
                                                        ip_max_users_per_day: prev.private_config?.anti_fraud?.ip_max_users_per_day ?? 15,
                                                        ip_block_mode: prev.private_config?.anti_fraud?.ip_block_mode ?? 'hard',
                                                    }
                                                }
                                            }));
                                            setIsDirty(true);
                                        }}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                                </label>
                            </div>

                            {campaign.private_config?.anti_fraud?.ip_limit_enabled !== false && (

                                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Số lượt tối đa / IP / ngày
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min={1}
                                                max={1000}
                                                value={campaign.private_config?.anti_fraud?.ip_max_users_per_day ?? 15}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value, 10) || 15;
                                                    setCampaign(prev => ({
                                                        ...prev,
                                                        private_config: {
                                                            ...(prev.private_config || {}),
                                                            anti_fraud: {
                                                                ...(prev.private_config?.anti_fraud || {}),
                                                                ip_max_users_per_day: val,
                                                            }
                                                        }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                className={`${inputClass} w-24`}
                                            />
                                            <span className="text-sm text-gray-500">lượt / IP / ngày</span>
                                        </div>
                                        <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
                                            💡 <strong>Mẹo:</strong> Nếu nhiều người chơi dùng chung một mạng Wi-Fi (như tại sự kiện, hội chợ hoặc quán café), hãy tăng giới hạn này hoặc bật <strong>Chế độ Kiosk</strong> ở trên để tránh bị chặn nhầm.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-2">Chế độ chặn khi vượt ngưỡng</label>
                                        <div className="space-y-2">
                                            {[
                                                { value: 'soft', label: '🟡 Mềm (Soft)', desc: 'Ghi log cảnh báo, không hiện thông báo lỗi cho user' },
                                                { value: 'hard', label: '🔴 Cứng (Hard)', desc: 'Chặn hoàn toàn, trả lỗi IP_LIMIT_EXCEEDED' },
                                            ].map(({ value, label, desc }) => (
                                                <label key={value} className={`flex items-start gap-2 rounded-lg border-2 p-2.5 cursor-pointer transition-all ${
                                                    (campaign.private_config?.anti_fraud?.ip_block_mode ?? 'hard') === value
                                                        ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
                                                }`}>
                                                    <input
                                                        type="radio"
                                                        name="ip_block_mode"
                                                        value={value}
                                                        checked={(campaign.private_config?.anti_fraud?.ip_block_mode ?? 'hard') === value}
                                                        onChange={() => {
                                                            setCampaign(prev => ({
                                                                ...prev,
                                                                private_config: {
                                                                    ...(prev.private_config || {}),
                                                                    anti_fraud: {
                                                                        ...(prev.private_config?.anti_fraud || {}),
                                                                        ip_block_mode: value as 'soft' | 'hard',
                                                                    }
                                                                }
                                                            }));
                                                            setIsDirty(true);
                                                        }}
                                                        className="mt-0.5 accent-red-500"
                                                    />
                                                    <div>
                                                        <div className="text-xs font-semibold text-gray-800">{label}</div>
                                                        <div className="text-xs text-gray-500">{desc}</div>
                                                    </div>
                                                </label>

                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Chặn IP thủ công ── */}
                            <div className="relative">
                                {/* Lock overlay khi chưa đủ gói */}
                                {!isIpBlockAllowed && (
                                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl bg-gray-100/85 backdrop-blur-[1px] p-5 text-center">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-xl shadow-sm">🔒</div>
                                        <h4 className="mt-2 font-bold text-gray-900 text-sm">Tính năng Chặn IP thủ công</h4>
                                        <p className="mt-1 max-w-xs text-xs text-gray-500 leading-relaxed font-semibold">
                                            Yêu cầu Hạng Vàng trở lên (ví ≥ 500 xu, phí 30 xu/ngày khi chạy).
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => openLockNotice('Chặn IP thủ công', 500, 'Hạng Vàng')}
                                            className="mt-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 px-4 py-2 text-[10px] font-bold text-white shadow-md hover:shadow-lg transition-all uppercase tracking-wide flex items-center gap-1 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                                        >
                                            💎 Mở khóa tính năng
                                        </button>
                                    </div>
                                )}
                                <BlockedIpManager
                                    blockedIps={campaign.private_config?.anti_fraud?.blocked_ips || []}
                                    onChange={(newList) => {
                                        if (!isIpBlockAllowed) return;
                                        setCampaign(prev => ({
                                            ...prev,
                                            private_config: {
                                                ...(prev.private_config || {}),
                                                anti_fraud: {
                                                    ...(prev.private_config?.anti_fraud || {}),
                                                    blocked_ips: newList,
                                                }
                                            }
                                        }));
                                        setIsDirty(true);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* 🔗 Webhook Integration */}
                    <div className="relative rounded-2xl border border-gray-150 p-5 bg-gray-50/50">
                        {/* Lock Overlay */}
                        {!isWebhookAllowed && (
                            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl bg-gray-100/80 backdrop-blur-[0.5px] p-6 text-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-2xl shadow-sm animate-pulse">
                                    🔒
                                </div>
                                <h4 className="mt-3 font-bold text-gray-900 text-sm">Tính năng Webhook Integration bị khóa</h4>
                                <p className="mt-1 max-w-md text-xs text-gray-500 leading-relaxed font-semibold">
                                    Yêu cầu Hạng Vàng trở lên (ví ≥ 500 xu, phí 30 xu/ngày khi chạy).
                                </p>
                                <button
                                    type="button"
                                    onClick={() => openLockNotice('Webhook Integration', 500, 'Hạng Vàng')}
                                    className="mt-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 px-5 py-2.5 text-[11px] font-bold text-white shadow-md hover:shadow-lg transition-all uppercase tracking-wide flex items-center gap-1.5 transform hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
                                >
                                    💎 Mở khóa tính năng
                                </button>
                            </div>
                        )}

                        <div className={`space-y-4 ${!isWebhookAllowed ? 'pointer-events-none select-none blur-[0.5px]' : ''}`}>
                            <div className="flex items-center gap-2">
                                <span className="text-lg">🔗</span>
                                <div className="text-sm font-semibold text-gray-700">Webhook Integration</div>
                            </div>
                            <p className="text-xs text-gray-500 -mt-2">Đồng bộ dữ liệu khách hàng tham gia chiến dịch và quà tặng trúng thưởng theo thời gian thực tới máy chủ hoặc hệ thống CRM của bạn.</p>

                            <div className="grid grid-cols-1 gap-4">
                                <Field label="Webhook Endpoint URL">
                                    <input
                                        type="url"
                                        placeholder="https://your-crm-api.com/webhooks/leads"
                                        value={campaign.public_config?.webhook_url || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setCampaign(prev => ({
                                                ...prev,
                                                public_config: {
                                                    ...(prev.public_config || {}),
                                                    webhook_url: val,
                                                }
                                            }));
                                            setIsDirty(true);
                                        }}
                                        className={inputClass}
                                    />
                                </Field>

                                <div className="space-y-2">
                                    <label className="text-xs font-medium text-gray-600 block">Sự kiện kích hoạt (Event Triggers)</label>
                                    <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={campaign.public_config?.webhook_events?.player_registered !== false}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setCampaign(prev => ({
                                                        ...prev,
                                                        public_config: {
                                                            ...(prev.public_config || {}),
                                                            webhook_events: {
                                                                ...(prev.public_config?.webhook_events || {}),
                                                                player_registered: checked,
                                                            }
                                                        }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            👤 Người chơi đăng ký
                                        </label>

                                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={campaign.public_config?.webhook_events?.gift_won !== false}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setCampaign(prev => ({
                                                        ...prev,
                                                        public_config: {
                                                            ...(prev.public_config || {}),
                                                            webhook_events: {
                                                                ...(prev.public_config?.webhook_events || {}),
                                                                gift_won: checked,
                                                            }
                                                        }
                                                    }));
                                                    setIsDirty(true);
                                                }}
                                                className="w-4 h-4 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                                            />
                                            🎁 Trúng phần thưởng
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                        {isDirty && <span className="text-xs text-amber-600 font-medium">● Có thay đổi chưa lưu</span>}
                        <button type="button" onClick={() => id ? setActiveTab('overview') : navigate('/campaigns')} className="rounded-lg border border-gray-300 px-5 py-2 text-sm hover:bg-gray-50">Hủy</button>
                        <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-5 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Đang lưu...' : id ? 'Lưu cài đặt' : 'Tạo chiến dịch'}</button>
                    </div>
                </form>
                </div>
            )}

            {showAddGameModal && (
                <Modal title="Thêm trò chơi từ mẫu" onClose={() => setShowAddGameModal(false)}>
                    <div className="space-y-4">
                        <Field label="Tên trò chơi">
                            <input type="text" value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="Lucky Wheel Game" className={inputClass} />
                        </Field>
                        <Field label="Mẫu trò chơi">
                            <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className={inputClass}>
                                <option value="">Chọn một mẫu...</option>
                                {gameTemplates
                                    .filter((template) => hasGameType(getGameTypeCode(template)))
                                    .map((template) => (
                                        <option key={template.id} value={template.id}>{getGameLabel(template)} ({getGameTypeCode(template)})</option>
                                    ))}
                            </select>
                        </Field>
                    </div>
                    <div className="mt-6 flex justify-end gap-2">
                        <button onClick={() => setShowAddGameModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Hủy</button>
                        <button onClick={handleAddGame} disabled={!selectedTemplate || !gameName} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">Thêm trò chơi</button>
                    </div>
                </Modal>
            )}

            {showUploadModal && isSuperAdmin && (
                <Modal title="Tải game ZIP lên" onClose={() => setShowUploadModal(false)}>
                    <form onSubmit={handleUploadGame}>
                        <Field label="File ZIP trò chơi">
                            <input type="file" name="gameZip" accept=".zip" className={inputClass} />
                        </Field>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" onClick={() => setShowUploadModal(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Hủy</button>
                            <button type="submit" disabled={uploading} className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50">{uploading ? 'Đang tải...' : 'Tải lên'}</button>
                        </div>
                    </form>
                </Modal>
            )}

            {editingGame && (
                <GameConfigModal
                    game={editingGame}
                    onClose={() => setEditingGame(null)}
                    onSave={async (updatedData) => {
                        try {
                            const res = await api.updateGameInstance(editingGame.id, updatedData);
                            setGameInstances(prev => prev.map(g => g.id === editingGame.id ? { ...g, ...res } : g));
                            setEditingGame(null);
                            alert('Cấu hình trò chơi đã được cập nhật thành công!');
                        } catch (err: any) {
                            console.error(err);
                            alert('Lỗi khi cập nhật cấu hình: ' + (err.message || 'Lỗi không xác định'));
                        }
                    }}
                />
            )}

            {lockedFeatureInfo && (
                <Modal title="Tính năng yêu cầu nâng cấp hạng ví" onClose={() => setLockedFeatureInfo(null)}>
                    <div className="space-y-4">
                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                            <h4 className="font-bold text-amber-900 text-sm">🔒 Tính năng: {lockedFeatureInfo.name}</h4>
                            <p className="mt-2 text-xs text-amber-800 leading-relaxed font-semibold">
                                Tính năng này yêu cầu tài khoản đạt <strong>{lockedFeatureInfo.requiredLevel}</strong> (số dư ví tối thiểu từ <strong>{lockedFeatureInfo.requiredXu} xu</strong>).
                            </p>
                        </div>

                        <div className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500 font-medium">Số dư ví của bạn:</span>
                                <span className={`font-bold ${walletBalance !== null && walletBalance >= lockedFeatureInfo.requiredXu ? 'text-green-600' : 'text-orange-500'}`}>
                                    {walletBalance !== null ? `${walletBalance} xu` : '...'}
                                </span>
                            </div>
                            {walletBalance !== null && walletBalance < lockedFeatureInfo.requiredXu && (
                                <div className="mt-3 text-[11px] text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-2.5 leading-relaxed font-semibold">
                                    ⚠️ Bạn còn thiếu <strong>{lockedFeatureInfo.requiredXu - walletBalance} xu</strong> để mở khóa hạng này. Vui lòng nạp thêm xu để mở khóa tính năng.
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button 
                                onClick={() => setLockedFeatureInfo(null)} 
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                            >
                                Đóng
                            </button>
                            <Link
                                to="/billing"
                                className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-4 py-2 text-sm text-white font-bold flex items-center justify-center cursor-pointer"
                            >
                                💳 Nạp thêm xu
                            </Link>
                        </div>
                    </div>
                </Modal>
            )}
        </Layout>
    );
}

function Th({ children }: { children: React.ReactNode }) {
    return <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{children}</th>;
}

function Td({ children, strong = false }: { children: React.ReactNode; strong?: boolean }) {
    return <td className={`px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{children}</td>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
        </label>
    );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
    return (
        <label className="flex items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-700">
            <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
            {label}
        </label>
    );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100">Close</button>
                </div>
                {children}
            </div>
        </div>
    );
}

function GiftTable({ gifts, compact = false }: { gifts: Gift[]; compact?: boolean }) {
    if (gifts.length === 0) {
        return <EmptyPanel title="Chưa có quà tặng" description="Chưa có quà tặng nào được gắn vào chiến dịch này." />;
    }

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="font-semibold text-gray-900">{compact ? 'Quà tặng gần đây' : 'Kho quà tặng'}</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <Th>Quà tặng</Th>
                            <Th>Đã nhập</Th>
                            <Th>Đã phát</Th>
                            <Th>Đã nhận</Th>
                            <Th>Còn lại</Th>
                            {!compact && <Th>Hiệu lực</Th>}
                            <Th>Trạng thái</Th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {gifts.map((gift) => (
                            <tr key={gift.id}>
                                <Td strong>
                                    <div>{gift.name}</div>
                                    <div className="text-xs font-normal text-gray-500">{gift.code}</div>
                                </Td>
                                <Td>{metricValue(gift.total_quantity)}</Td>
                                <Td>{metricValue(gift.assigned_quantity)}</Td>
                                <Td>{metricValue(gift.claimed_quantity)}</Td>
                                <Td>{metricValue(getRemaining(gift))}</Td>
                                {!compact && <Td>{formatDate(gift.valid_until)}</Td>}
                                <Td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${gift.status === 'active' ? 'bg-green-100 text-green-800' : gift.status === 'out_of_stock' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>{gift.status}</span></Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function PlayerTable({ players, compact = false }: { players: Player[]; compact?: boolean }) {
    if (players.length === 0) {
        return <EmptyPanel title="No players" description="No lead or player records have been captured yet." />;
    }

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="font-semibold text-gray-900">{compact ? 'Recent Players' : 'Players And Leads'}</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <Th>Player</Th>
                            <Th>Phone</Th>
                            {!compact && <Th>Email</Th>}
                            <Th>Identity</Th>
                            <Th>Last Seen</Th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {players.map((player) => (
                            <tr key={player.id}>
                                <Td strong>{player.full_name || 'Anonymous'}</Td>
                                <Td>{player.phone || '-'}</Td>
                                {!compact && <Td>{player.email || '-'}</Td>}
                                <Td>{player.identity_type || '-'}</Td>
                                <Td>{formatDateTime(player.last_seen_at)}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const METRIC_KEY_LABELS: Record<string, { label: string; color: string }> = {
    finished:  { label: 'Hoàn thành', color: 'bg-teal-500' },
    pending:   { label: 'Đang chờ',   color: 'bg-yellow-400' },
    started:   { label: 'Đang chơi',  color: 'bg-blue-400' },
    abandoned: { label: 'Bỏ giữa chừng', color: 'bg-gray-400' },
    win:       { label: 'Trúng thưởng', color: 'bg-green-500' },
    lose:      { label: 'Không trúng',  color: 'bg-gray-300' },
};

function MetricMap({ title, values, wide = false }: { title: string; values?: Record<string, number>; wide?: boolean }) {
    const entries = Object.entries(values || {});
    const total = entries.reduce((sum, [, v]) => sum + (v || 0), 0);
    return (
        <div className={`rounded-lg border border-gray-200 bg-white p-5 shadow-sm ${wide ? 'xl:col-span-2' : ''}`}>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {entries.length === 0 ? (
                <p className="mt-3 text-sm text-gray-400 italic">Chưa có dữ liệu.</p>
            ) : (
                <div className="mt-4 space-y-3">
                    {entries.map(([key, value]) => {
                        const meta = METRIC_KEY_LABELS[key];
                        const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                        return (
                            <div key={key}>
                                <div className="mb-1.5 flex items-center justify-between text-sm">
                                    <span className="text-gray-600">{meta?.label || key}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-400">{pct}%</span>
                                        <span className="min-w-[2rem] text-right font-semibold text-gray-900">{metricValue(value)}</span>
                                    </div>
                                </div>
                                <div className="h-2 rounded-full bg-gray-100">
                                    <div
                                        className={`h-2 rounded-full transition-all ${meta?.color || 'bg-blue-500'}`}
                                        style={{ width: `${Math.max(2, pct)}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

interface GameConfigModalProps {
    game: GameInstance;
    onClose: () => void;
    onSave: (data: { name: string; config: any; assets: any }) => Promise<void>;
}
function GameConfigModal({ game, onClose, onSave }: GameConfigModalProps) {
    const { hasFeature } = useEntitlements();
    const canCustomBg = hasFeature('custom_background');

    const [name, setName] = useState(game.name || '');
    const [tagline, setTagline] = useState((game.config as any)?.tagline || '');
    const [theme, setTheme] = useState((game.config as any)?.theme || 'red');
    const [logoUrl, setLogoUrl] = useState((game.assets as any)?.logo_url || '');
    const [bgMobileUrl, setBgMobileUrl] = useState((game.assets as any)?.background_mobile_url || (game.assets as any)?.background_url || '');
    const [bgDesktopUrl, setBgDesktopUrl] = useState((game.assets as any)?.background_desktop_url || (game.assets as any)?.background_url || '');
    const [saving, setSaving] = useState(false);
    const [gridSize, setGridSize] = useState((game.config as any)?.grid_size || 16);

    // States cho việc upload
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBgMobile, setUploadingBgMobile] = useState(false);
    const [uploadingBgDesktop, setUploadingBgDesktop] = useState(false);
    const [uploadingCardBack, setUploadingCardBack] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const [bgMobileError, setBgMobileError] = useState<string | null>(null);
    const [bgDesktopError, setBgDesktopError] = useState<string | null>(null);
    const [cardBackError, setCardBackError] = useState<string | null>(null);
    const [cardBackUrl, setCardBackUrl] = useState((game.assets as any)?.card_back_url || '');

    // System assets — fallback khi chiến dịch chưa upload logo/background
    const [sysDefaultBg, setSysDefaultBg]     = useState<string>('');
    const [sysDefaultIcon, setSysDefaultIcon] = useState<string>('');
    useEffect(() => {
        const code = getGameTypeCode(game.game_template); // 'flip_card' | 'lucky_wheel' | ...
        const bgKey = `default_bg_${code}`; // e.g. "default_bg_flip_card"
        fetch('/api/public/v1/system/assets')
            .then(r => r.json())
            .then(d => {
                const sa = d?.data || {};
                if (sa[bgKey])       setSysDefaultBg(sa[bgKey]);
                if (sa.default_icon) setSysDefaultIcon(sa.default_icon);
            })
            .catch(() => { /* system assets optional */ });
    }, [game.game_template]);


    const themes = [
        { id: 'default', name: 'Mặc định',      color: '#64748b', bg: 'bg-slate-500',   border: 'border-slate-300',   gradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' },
        { id: 'red',     name: 'Đỏ Lễ Hội',     color: '#E11D48', bg: 'bg-rose-500',    border: 'border-rose-300',    gradient: 'linear-gradient(135deg, #1e0b11 0%, #3d1420 50%, #1e0b11 100%)' },
        { id: 'blue',    name: 'Xanh Công Nghệ', color: '#2563EB', bg: 'bg-blue-500',    border: 'border-blue-300',    gradient: 'linear-gradient(135deg, #071432 0%, #0c204e 50%, #071432 100%)' },
        { id: 'gold',    name: 'Vàng Hoàng Kim', color: '#D97706', bg: 'bg-amber-500',   border: 'border-amber-300',   gradient: 'linear-gradient(135deg, #1b1307 0%, #2f210d 50%, #1b1307 100%)' },
        { id: 'green',   name: 'Xanh Sinh Thái', color: '#059669', bg: 'bg-emerald-500', border: 'border-emerald-300', gradient: 'linear-gradient(135deg, #061a12 0%, #0b3021 50%, #061a12 100%)' },
        { id: 'cyberpunk', name: 'Cyberpunk Neon',  color: '#8A2EFF', bg: 'bg-purple-600',  border: 'border-purple-300',  gradient: 'linear-gradient(135deg, #120018 0%, #200030 50%, #120018 100%)' },
        { id: 'sunset',  name: 'Sunset Glow',   color: '#F97316', bg: 'bg-orange-500',  border: 'border-orange-300',  gradient: 'linear-gradient(135deg, #1e0f07 0%, #3b1e0f 50%, #1e0f07 100%)' },
        { id: 'violet',  name: 'Midnight Violet', color: '#8B5CF6', bg: 'bg-violet-600',  border: 'border-violet-300',  gradient: 'linear-gradient(135deg, #0d061e 0%, #1c0e3a 50%, #0d061e 100%)' },
    ];


    const currentThemeInfo = themes.find(t => t.id === theme) || themes[0];
    const gameTypeCode = getGameTypeCode(game.game_template);
    const isFlipCard = gameTypeCode === 'flip_card';
    const isScratchCard = gameTypeCode === 'scratch_card';
    const isEggPick = gameTypeCode === 'egg_pick';

    let defaultTitle = 'LUCKY WHEEL';
    let defaultTagline = 'Quay là trúng quà cực hấp dẫn!';
    let buttonLabel = 'QUAY NGAY!';
    let subLabel = 'Mỗi người chơi có 1 lượt quay';

    if (isFlipCard) {
        defaultTitle = 'LẬT THẺ BÀI';
        defaultTagline = 'Lật thẻ trúng quà cực đã!';
        buttonLabel = 'LẬT THẺ NGAY!';
        subLabel = 'Mỗi người chơi có 1 lượt lật thẻ';
    } else if (isScratchCard) {
        defaultTitle = 'THẺ CÀO MAY MẮN';
        defaultTagline = 'Cào liền tay, nhận ngay quà khủng!';
        buttonLabel = 'CÀO THẺ NGAY!';
        subLabel = 'Mỗi người chơi có 1 lượt cào thẻ';
    } else if (isEggPick) {
        defaultTitle = 'ĐẬP TRỨNG VÀNG';
        defaultTagline = 'Gõ búa đập trứng, hứng lộc đầu xuân!';
        buttonLabel = 'ĐẬP TRỨNG NGAY!';
        subLabel = 'Mỗi người chơi có 1 lượt đập trứng';
    }

    const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLogoError(null);
        // Kiểm tra giới hạn size 1MB (1024 * 1024 bytes)
        if (file.size > 1024 * 1024) {
            setLogoError('Dung lượng Logo vượt quá 1MB. Vui lòng chọn ảnh nhẹ hơn.');
            return;
        }

        setUploadingLogo(true);
        try {
            const res = await (api as any).uploadGameAsset(file, 'logo');
            setLogoUrl(res.url);
        } catch (err: any) {
            setLogoError(err.message || 'Không thể upload logo. Hãy thử lại.');
        } finally {
            setUploadingLogo(false);
        }
    };


    const handleBgMobileFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBgMobileError(null);
        if (file.size > 2 * 1024 * 1024) {
            setBgMobileError('Dung lượng vượt quá 2MB. Vui lòng chọn ảnh nhẹ hơn.');
            return;
        }
        setUploadingBgMobile(true);
        try {
            const res = await (api as any).uploadGameAsset(file, 'background_mobile');
            setBgMobileUrl(res.url);
        } catch (err: any) {
            setBgMobileError(err.message || 'Không thể upload. Hãy thử lại.');
        } finally {
            setUploadingBgMobile(false);
        }
    };

    const handleBgDesktopFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBgDesktopError(null);
        if (file.size > 3 * 1024 * 1024) {
            setBgDesktopError('Dung lượng vượt quá 3MB. Vui lòng chọn ảnh nhẹ hơn.');
            return;
        }
        setUploadingBgDesktop(true);
        try {
            const res = await (api as any).uploadGameAsset(file, 'background_desktop');
            setBgDesktopUrl(res.url);
        } catch (err: any) {
            setBgDesktopError(err.message || 'Không thể upload. Hãy thử lại.');
        } finally {
            setUploadingBgDesktop(false);
        }
    };

    const handleCardBackFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCardBackError(null);
        if (file.size > 1024 * 1024) {
            setCardBackError('Dung lượng ảnh vượt quá 1MB. Vui lòng chọn ảnh nhẹ hơn.');
            return;
        }
        setUploadingCardBack(true);
        try {
            const res = await (api as any).uploadGameAsset(file, 'card_back');
            setCardBackUrl(res.url);
        } catch (err: any) {
            setCardBackError(err.message || 'Không thể upload ảnh. Hãy thử lại.');
        } finally {
            setUploadingCardBack(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSave({
                name,
                config: { ...game.config, tagline, theme, grid_size: Number(gridSize) },
                assets: {
                    logo_url: logoUrl,
                    background_mobile_url: bgMobileUrl,
                    background_desktop_url: bgDesktopUrl,
                    card_back_url: cardBackUrl,
                }
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 overflow-y-auto">
            <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col md:flex-row my-8 overflow-hidden">
                
                {/* CỘT TRÁI: CÀI ĐẶT & UPLOADS */}
                <div className="flex-1 p-6 md:p-8 flex flex-col border-r border-gray-100 max-h-[85vh] overflow-y-auto">
                    <div className="mb-5 flex items-center justify-between border-b border-gray-100 pb-4">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">Thiết lập giao diện</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Tùy biến thương hiệu, màu sắc, logo và hình nền cho chiến dịch game.</p>
                        </div>
                        <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition md:hidden">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5 flex-1 max-w-[600px] mx-auto w-full">
                        {/* Tên & Tagline */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Tên hiển thị trò chơi</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition"
                                    placeholder="Ví dụ: Vòng Quay May Mắn"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Tagline / Slogan dẫn dắt</label>
                                <input
                                    type="text"
                                    value={tagline}
                                    onChange={(e) => setTagline(e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition"
                                    placeholder="Ví dụ: Quay ngay trúng lớn!"
                                />
                            </div>
                        </div>

                        {getGameTypeCode(game.game_template) === 'flip_card' && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Số lượng thẻ bài (Kích thước lưới)</label>
                                <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                                    <span className="text-2xl">🃏</span>
                                    <div>
                                        <div className="text-sm font-bold text-emerald-800">4 thẻ — Lưới 2×2 (Cố định)</div>
                                        <div className="text-[11px] text-emerald-600 mt-0.5">Người chơi lật tất cả 4 thẻ để khám phá kết quả. Có 1 cặp thẻ trúng thưởng ẩn trong lưới.</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bộ chọn Theme màu sắc */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Chủ đề màu sắc chủ đạo (Preset Theme)</label>
                            <div className="grid grid-cols-3 gap-2.5">
                                {themes.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setTheme(t.id)}
                                        className={`relative flex flex-col items-center justify-center rounded-xl border p-2.5 transition-all ${
                                            theme === t.id
                                                ? 'border-emerald-600 bg-emerald-50/20 ring-2 ring-emerald-500/10'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {t.id === 'default' ? (
                                            // Icon đặc biệt cho Mặc định
                                            <span className="h-7 w-7 rounded-full shadow-sm flex items-center justify-center mb-1.5 overflow-hidden" style={{background:'linear-gradient(135deg,#3b82f6,#8b5cf6,#f59e0b,#10b981)'}}>
                                                {theme === t.id ? (
                                                    <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <span className="text-[10px] text-white font-black">A</span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className={`h-7 w-7 rounded-full ${t.bg} shadow-sm flex items-center justify-center text-white mb-1.5`}>
                                                {theme === t.id && (
                                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </span>
                                        )}
                                        <span className="text-[10px] font-bold text-gray-700">{t.name}</span>
                                        {t.id === 'default' && (
                                            <span className="text-[9px] text-blue-500 font-medium mt-0.5 leading-tight text-center">(Bg hệ thống)</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* UPLOAD LOGO THƯƠNG HIỆU */}
                        <div className="border-t border-gray-100 pt-4">
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">Logo Thương Hiệu (Kích thước khuyên dùng: 500x250 px, tối đa 1MB)</label>
                            <div className="flex gap-3 items-center mt-2">
                                <label className="flex flex-col items-center justify-center flex-1 h-20 rounded-lg border-2 border-dashed border-gray-300 hover:border-emerald-500 hover:bg-emerald-50/10 cursor-pointer transition relative group">
                                    <div className="flex flex-col items-center justify-center py-2 text-center px-4">
                                        {uploadingLogo ? (
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <svg className="animate-spin h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                <span>Đang tải Logo...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <svg className="h-5 w-5 text-gray-400 group-hover:text-emerald-600 transition mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                <span className="text-xs font-semibold text-gray-600 group-hover:text-emerald-700 transition">Tải Logo từ thiết bị</span>
                                            </>
                                        )}
                                    </div>
                                    <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp" className="hidden" onChange={handleLogoFileChange} disabled={uploadingLogo} />
                                </label>
                                
                                <div className="w-24">
                                    <input
                                        type="text"
                                        value={logoUrl}
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-[11px] focus:border-emerald-500 outline-none transition font-mono"
                                        placeholder="Hoặc URL..."
                                    />
                                    {logoUrl && (
                                        <button type="button" onClick={() => setLogoUrl('')} className="text-[10px] text-red-500 font-bold hover:underline block mt-1">Xóa Logo</button>
                                    )}
                                </div>
                            </div>
                            {logoError && <p className="text-xs text-red-500 mt-1 font-semibold">{logoError}</p>}
                        </div>

                        {/* UPLOAD BACKGROUND HÌNH NỀN — Chỉ gói cao cấp (event_15d+) */}
                        <div className="border-t border-gray-100 pt-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    🖼️ Hình nền chiến dịch
                                    {!canCustomBg && (
                                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                                            ⭐ Sự kiện trở lên
                                        </span>
                                    )}
                                </label>
                                {!canCustomBg && (
                                    <a href="/billing" className="text-[11px] text-emerald-600 font-bold hover:underline">Nâng cấp →</a>
                                )}
                            </div>

                            {!canCustomBg ? (
                                /* Locked state */
                                <div className="rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/50 px-4 py-5 text-center">
                                    <div className="text-2xl mb-1">🔒</div>
                                    <p className="text-sm font-bold text-amber-800">Tính năng dành cho gói Sự kiện trở lên</p>
                                    <p className="text-xs text-amber-600 mt-1">Tải ảnh nền riêng cho Mobile & Desktop · Từ 299.000đ/15 ngày</p>
                                </div>
                            ) : (
                                /* Unlocked: 2 fields side-by-side */
                                <div className="grid grid-cols-2 gap-3">
                                    {/* --- Mobile Background --- */}
                                    <div>
                                        <p className="text-[11px] font-bold text-gray-500 mb-1.5 flex items-center gap-1">
                                            📱 Mobile <span className="font-normal opacity-70">(9:16 · tối đa 2MB)</span>
                                        </p>
                                        <label className="flex flex-col items-center justify-center w-full h-20 rounded-lg border-2 border-dashed border-sky-200 hover:border-sky-500 hover:bg-sky-50/10 cursor-pointer transition group">
                                            <div className="flex flex-col items-center justify-center py-2 text-center px-2">
                                                {uploadingBgMobile ? (
                                                    <svg className="animate-spin h-5 w-5 text-sky-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                                ) : (
                                                    <>
                                                        <span className="text-lg">📱</span>
                                                        <span className="text-[10px] font-semibold text-gray-500 group-hover:text-sky-600 transition">Tải ảnh Mobile</span>
                                                    </>
                                                )}
                                            </div>
                                            <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={handleBgMobileFileChange} disabled={uploadingBgMobile} />
                                        </label>
                                        <input type="text" value={bgMobileUrl} onChange={(e) => setBgMobileUrl(e.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-[10px] focus:border-sky-500 outline-none font-mono mt-1.5"
                                            placeholder="hoặc dán URL..." />
                                        {bgMobileUrl && (
                                            <div className="mt-1.5 relative rounded-lg overflow-hidden border border-sky-200" style={{height:'60px'}}>
                                                <img src={bgMobileUrl} alt="mobile preview" className="w-full h-full object-cover" />
                                                <button type="button" onClick={() => setBgMobileUrl('')}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">✕</button>
                                            </div>
                                        )}
                                        {bgMobileError && <p className="text-[10px] text-red-500 mt-1 font-semibold">{bgMobileError}</p>}
                                    </div>

                                    {/* --- Desktop Background --- */}
                                    <div>
                                        <p className="text-[11px] font-bold text-gray-500 mb-1.5 flex items-center gap-1">
                                            🖥️ Desktop <span className="font-normal opacity-70">(16:9 · tối đa 3MB)</span>
                                        </p>
                                        <label className="flex flex-col items-center justify-center w-full h-20 rounded-lg border-2 border-dashed border-indigo-200 hover:border-indigo-500 hover:bg-indigo-50/10 cursor-pointer transition group">
                                            <div className="flex flex-col items-center justify-center py-2 text-center px-2">
                                                {uploadingBgDesktop ? (
                                                    <svg className="animate-spin h-5 w-5 text-indigo-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                                ) : (
                                                    <>
                                                        <span className="text-lg">🖥️</span>
                                                        <span className="text-[10px] font-semibold text-gray-500 group-hover:text-indigo-600 transition">Tải ảnh Desktop</span>
                                                    </>
                                                )}
                                            </div>
                                            <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={handleBgDesktopFileChange} disabled={uploadingBgDesktop} />
                                        </label>
                                        <input type="text" value={bgDesktopUrl} onChange={(e) => setBgDesktopUrl(e.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-[10px] focus:border-indigo-500 outline-none font-mono mt-1.5"
                                            placeholder="hoặc dán URL..." />
                                        {bgDesktopUrl && (
                                            <div className="mt-1.5 relative rounded-lg overflow-hidden border border-indigo-200" style={{height:'60px'}}>
                                                <img src={bgDesktopUrl} alt="desktop preview" className="w-full h-full object-cover" />
                                                <button type="button" onClick={() => setBgDesktopUrl('')}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">✕</button>
                                            </div>
                                        )}
                                        {bgDesktopError && <p className="text-[10px] text-red-500 mt-1 font-semibold">{bgDesktopError}</p>}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* UPLOAD ẢNH MẶT SAU THẺ BÀI — chỉ hiện cho flip_card */}
                        {isFlipCard && (
                        <div className="border-t border-gray-100 pt-4">
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                                🃏 Ảnh Mặt Sau Thẻ Bài <span className="text-purple-600">(Flip Card)</span>
                                <span className="ml-1 normal-case font-normal text-gray-400">— Tỷ lệ 3:4 dọc, tối đa 1MB</span>
                            </label>
                            <p className="text-[11px] text-gray-400 mb-2">Nếu không tải, thẻ sẽ dùng hoạ tiết mandala mặc định.</p>
                            <div className="flex gap-3 items-center mt-1">
                                <label className="flex flex-col items-center justify-center flex-1 h-20 rounded-lg border-2 border-dashed border-purple-200 hover:border-purple-500 hover:bg-purple-50/10 cursor-pointer transition relative group">
                                    <div className="flex flex-col items-center justify-center py-2 text-center px-4">
                                        {uploadingCardBack ? (
                                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                                <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                <span>Đang tải ảnh thẻ...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <span className="text-2xl mb-0.5">🃏</span>
                                                <span className="text-xs font-semibold text-gray-600 group-hover:text-purple-700 transition">Tải ảnh mặt sau</span>
                                            </>
                                        )}
                                    </div>
                                    <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp" className="hidden" onChange={handleCardBackFileChange} disabled={uploadingCardBack} />
                                </label>

                                <div className="w-24 flex flex-col gap-1">
                                    <input
                                        type="text"
                                        value={cardBackUrl}
                                        onChange={(e) => setCardBackUrl(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-[11px] focus:border-purple-500 outline-none transition font-mono"
                                        placeholder="Hoặc URL..."
                                    />
                                    {cardBackUrl && (
                                        <>
                                            <img src={cardBackUrl} alt="preview" className="w-full h-16 object-cover rounded-lg border border-purple-200" />
                                            <button type="button" onClick={() => setCardBackUrl('')} className="text-[10px] text-red-500 font-bold hover:underline">Xóa ảnh</button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {cardBackError && <p className="text-xs text-red-500 mt-1 font-semibold">{cardBackError}</p>}
                        </div>
                        )}

                        {/* Nút hành động */}
                        <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 transition font-medium"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={saving || uploadingLogo || uploadingBgMobile || uploadingBgDesktop || uploadingCardBack}
                                className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50 transition flex items-center gap-1.5"
                            >
                                {saving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Đang lưu...
                                    </>
                                ) : 'Lưu & Áp Dụng'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* CỘT PHẢI: LIVE MOBILE MOCKUP PREVIEW */}
                <div className="w-full md:w-[360px] bg-slate-900 p-6 flex flex-col items-center justify-center border-l border-gray-800 relative">
                    <div className="absolute top-4 left-6 text-xs text-gray-400 font-bold uppercase tracking-wider flex items-center gap-1">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                        Live Preview (Xem thử thực tế)
                    </div>

                    <button onClick={onClose} className="absolute top-4 right-4 rounded-lg p-1.5 text-gray-400 hover:bg-gray-800 hover:text-white transition hidden md:block">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* MOCKUP ĐIỆN THOẠI CỰC PREMIUM */}
                    <div className="w-[280px] h-[520px] rounded-[36px] border-[6px] border-slate-700 bg-black shadow-2xl relative overflow-hidden flex flex-col items-center mt-6 transition-all duration-300">
                        {/* iPhone Notch */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-28 bg-slate-700 rounded-b-2xl z-20"></div>

                        {/* MÀN HÌNH GAME PREVIEW ĐỘNG */}
                        <div 
                            className="w-full h-full p-4 flex flex-col items-center justify-between text-white relative z-10 transition-all duration-500"
                            style={{
                                background: bgMobileUrl
                                    // 1. User đã upload ảnh riêng → dùng ảnh đó
                                    ? `url('${bgMobileUrl}') center center / cover no-repeat`
                                    : (theme === 'default' && sysDefaultBg)
                                        // 2. Theme "Mặc định" + hệ thống có bg → dùng bg hệ thống
                                        ? `url('${sysDefaultBg}') center center / cover no-repeat`
                                        // 3. Theme màu cụ thể → dùng gradient của theme đó
                                        : currentThemeInfo.gradient
                            }}
                        >
                            {/* Header Mockup */}
                            <div className="w-full text-center mt-4 flex flex-col items-center z-10">
                                {logoUrl ? (
                                    <img 
                                        src={logoUrl} 
                                        alt="Logo" 
                                        className="max-h-[44px] max-width-[120px] object-contain mb-2 animate-pulse"
                                        onError={(e) => {
                                            (e.target as HTMLElement).style.display = 'none';
                                        }}
                                    />
                                ) : sysDefaultIcon ? (
                                    <img 
                                        src={sysDefaultIcon}
                                        alt="System Logo"
                                        className="max-h-[36px] max-w-[100px] object-contain mb-2 opacity-80"
                                    />
                                ) : (
                                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center mb-2 border border-white/20">
                                        <span className="text-xs">LOGO</span>
                                    </div>
                                )}
                                <h4 className="text-sm font-black tracking-wide truncate max-w-[200px]" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
                                    {name || defaultTitle}
                                </h4>
                                <p className="text-[10px] text-white/80 line-clamp-2 max-w-[180px] mt-0.5 font-medium leading-tight" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                    {tagline || defaultTagline}
                                </p>
                            </div>

                            {/* DYNAMIC GAME PREVIEWS FOR BRAND MOCKUP */}
                            {isFlipCard && (
                                <div className="w-full my-auto px-4 flex flex-col gap-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        {[0, 1, 2, 3].map((i) => {
                                            const isFlipped = i === 0 || i === 3;
                                            const isPrizeCard = isFlipped;
                                            return (
                                                <div 
                                                    key={i} 
                                                    className={`aspect-[3/4] rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center relative overflow-hidden ${
                                                        isPrizeCard
                                                            ? 'bg-gradient-to-br from-amber-400 to-yellow-600 border-amber-300 shadow-lg scale-95' 
                                                            : 'bg-white/10 border-white/20 backdrop-blur-md shadow-md'
                                                    }`}
                                                    style={{
                                                        boxShadow: isPrizeCard ? `0 4px 16px ${currentThemeInfo.color}55` : undefined
                                                    }}
                                                >
                                                    {isPrizeCard ? (
                                                        <div className="flex flex-col items-center justify-center gap-1">
                                                            <span className="text-[22px]">🎁</span>
                                                            <span className="text-[7px] font-black text-white uppercase tracking-wide">Trúng!</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center gap-1">
                                                            <span className="text-[18px] opacity-80">🃏</span>
                                                            <span className="text-[7px] text-white/60 uppercase font-bold">Lật thẻ</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="flex justify-center gap-2 mt-1">
                                        {[0,1,2,3].map(i => (
                                            <div key={i} className={`w-2 h-2 rounded-full ${i < 2 ? 'bg-amber-400' : 'bg-white/30'}`} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isScratchCard && (
                                <div className="w-full my-auto px-3">
                                    <div className="w-full aspect-[1.8/1] rounded-2xl bg-white/5 border border-white/15 p-3 flex flex-col items-center justify-between relative overflow-hidden backdrop-blur-md">
                                        <div className="w-full text-center">
                                            <span className="text-[9px] font-bold tracking-widest text-white/40 uppercase">Thẻ Cào May Mắn</span>
                                        </div>
                                        
                                        <div className="w-full h-16 rounded-xl border-2 border-dashed border-amber-400/40 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative overflow-hidden group shadow-inner">
                                            <div className="flex flex-col items-center justify-center">
                                                <span className="text-[10px] font-extrabold text-amber-300/90 tracking-wide uppercase">Cào tại đây</span>
                                                <span className="text-[8px] text-white/40 font-medium">Sử dụng ngón tay</span>
                                            </div>
                                            <div className="absolute right-2 bottom-1 text-[20px] opacity-75">🪙</div>
                                        </div>

                                        <div className="w-full flex justify-between items-center text-[7px] text-white/50 px-1">
                                            <span>Mã lượt chơi: #EPX82</span>
                                            <span>100% TRÚNG QUÀ</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isEggPick && (
                                <div className="w-full my-auto px-2">
                                    <div className="grid grid-cols-3 gap-2.5">
                                        {[0, 1, 2].map((i) => {
                                            const isBroken = i === 1;
                                            return (
                                                <div 
                                                    key={i} 
                                                    className={`aspect-[3/4.2] rounded-2xl bg-white/5 border border-white/12 flex flex-col items-center justify-center relative p-1 transition-all ${
                                                        isBroken ? 'scale-105 bg-white/10 border-amber-400' : ''
                                                    }`}
                                                >
                                                    {isBroken ? (
                                                        <div className="flex flex-col items-center justify-center text-center">
                                                            <span className="text-[34px] animate-bounce">🐣</span>
                                                            <span className="text-[7px] font-black text-amber-300 mt-1 uppercase">Trúng!</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center text-center">
                                                            <span className="text-[34px] filter drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]">🥚</span>
                                                            <span className="text-[6px] text-white/40 mt-1 font-semibold uppercase">Chọn</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {!isFlipCard && !isScratchCard && !isEggPick && (
                                <div className="relative w-44 h-44 my-auto flex items-center justify-center">
                                    {/* Pointer */}
                                    <div className="absolute top-[-10px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[18px] border-t-red-500 drop-shadow-md z-20"></div>
                                    
                                    {/* Vòng Tròn Quay */}
                                    <div 
                                        className="w-full h-full rounded-full border-4 border-amber-400 shadow-xl relative overflow-hidden animate-[spin_10s_linear_infinite]"
                                        style={{
                                            background: `conic-gradient(
                                                #ef4444 0deg 45deg, 
                                                #f59e0b 45deg 90deg, 
                                                #22c55e 90deg 135deg, 
                                                #3b82f6 135deg 180deg, 
                                                #ef4444 180deg 225deg, 
                                                #f59e0b 225deg 270deg, 
                                                #22c55e 270deg 315deg, 
                                                #3b82f6 315deg 360deg
                                            )`
                                        }}
                                    >
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-[90%] h-[90%] rounded-full border border-white/20"></div>
                                        </div>
                                    </div>

                                    {/* Center Cap */}
                                    <div className="absolute h-9 w-9 rounded-full bg-slate-900 border-2 border-amber-400 flex items-center justify-center shadow-lg z-10 text-[7px] font-black text-amber-400">
                                        XGAME
                                    </div>
                                </div>
                            )}

                            {/* Nút Quay Mockup */}
                            <div className="w-full text-center mb-2 z-10">
                                <button 
                                    type="button" 
                                    className="w-full max-w-[180px] rounded-full py-2 text-xs font-black text-white tracking-wider shadow-lg transition transform hover:scale-105 duration-200"
                                    style={{
                                        background: `linear-gradient(135deg, ${currentThemeInfo.color}, #f59e0b)`
                                    }}
                                >
                                    {buttonLabel}
                                </button>
                                <span className="block text-[8px] text-white/40 mt-1 font-semibold">{subLabel}</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

